/**
 * Class CheckoutVideo
 * <The methods code is a copy from the TechDivision.Video Plugin>
 *
 * This class is responsible for handling the initialization and playback of embedded videos
 * on the checkout page. It listens for the 'checkoutVideoInitialized' event to trigger the
 * initialization process for video elements on the page. The class supports both YouTube and
 * Vimeo videos, providing functionality to manage video playback, consent handling, and
 * dynamic iframe creation for YouTube.
 *
 * Methods:
 *
 * - constructor(): Sets up the event listener for the 'checkoutVideoInitialized' event and
 *   triggers the initialization of videos after a short delay.
 *
 * - initializeVideo(): Finds and sets up the video elements on the page. Attaches event
 *   listeners to play buttons and handles video playback based on the video type (YouTube or Vimeo).
 *   For YouTube videos, it waits for user consent before creating and embedding the video iframe.
 *
 * - createYoutubeIframe(video): Creates and embeds a YouTube video iframe in place of the preview image.
 *   This method is called after the user gives consent to play the YouTube video. It configures the player
 *   with various options (autoplay, controls, etc.) based on the data attributes of the video element.
 *
 * - onPlayerReady(event): Callback function executed when the YouTube player is ready. It auto-plays the video
 *   once the player has been initialized.
 *
 * - dispatchVideoEvent(): Dispatches a custom 'playVideo' event to signal that a video has started playing.
 *
 * Usage:
 *
 * 1. Ensure the class is instantiated after the page has loaded and the necessary video elements are present.
 * 2. Dispatch the 'checkoutVideoInitialized' event to trigger the video initialization process.
 * 3. For YouTube videos, make sure user consent is handled properly before playback.
 *
 * Example:
 *
 * const checkoutVideo = new CheckoutVideo();
 * window.dispatchEvent(new Event('checkoutVideoInitialized'));
 */
export default class CheckoutVideo {

    constructor() {
        window.addEventListener('checkoutVideoInitialized', () => {
            setTimeout(() => {
                this.initializeVideo();
            }, 700)
        });
    }

    /**
     * Get youTube video consent status
     * @returns {boolean}
     */
    getYouTubeConsentStatus() {
        const services = UC_UI.getServicesBaseInfo()
        const youTubeService = services.find(service => service.id === "BJz7qNsdj-7")
        if (youTubeService) {
            return youTubeService.consent.status;
        }
        return false;
    }

    initializeVideo() {
        const embedVideoContentElements = document.querySelectorAll('[data-techdivision-embedvideo="wrapper"]');
        embedVideoContentElements.forEach((videoContentElement) => {
            const playButton = videoContentElement.querySelector('[data-techdivision-embedvideo="play"]');
            const previewImage = videoContentElement.querySelector('[data-techdivision-embedvideo="image"]');
            const videoType = videoContentElement.dataset.videotype;
            const video = videoContentElement.querySelector('[data-video="embedVideo"]');

            // Helper function to handle YouTube video consent and iframe creation
            const handleYouTubeConsent = () => {
                this.createYoutubeIframe(video);  // Aufruf der Methode innerhalb der Klasse
                window.removeEventListener('YoutubeVideoConsentGiven', handleYouTubeConsent);
            };

            if (previewImage && playButton) {
                // Play video on play button click
                playButton.addEventListener('click', () => {
                    playButton.style.display = 'none';
                    previewImage.style.display = 'none';

                    if (videoType === 'youtube') {
                        if (this.getYouTubeConsentStatus()) {
                            this.createYoutubeIframe(video);
                        } else {
                            window.addEventListener('YoutubeVideoConsentGiven', handleYouTubeConsent);
                        }
                    } else if (videoType === 'vimeo') {
                        video.src += '&autoplay=1';
                        // Display button when video has ended
                        video.addEventListener('ended', () => {
                            playButton.style.display = 'block';
                        }, {once: true});
                        this.dispatchVideoEvent();  // Aufruf der Methode innerhalb der Klasse
                    }
                }, {once: true}); // Ensures the event listener is only used once and then removed
            } else {
                // Handle videos without a preview image (e.g., auto-play)
                if (videoType === 'youtube') {
                    if (this.getYouTubeConsentStatus()) {
                        this.createYoutubeIframe(video);
                    } else {
                        window.addEventListener('YoutubeVideoConsentGiven', handleYouTubeConsent);
                    }
                }
            }
        });
    }

    /**
     * This function creates an <iframe> and YouTube player
     * @param video
     */
    createYoutubeIframe(video) {
        window.YT.ready(() => {
            const player = new YT.Player(video.id, {
                height: video.dataset.height,
                width: video.dataset.width,
                host: 'https://www.youtube-nocookie.com',
                videoId: video.dataset.videoid,
                playerVars: {
                    autoplay: 0,
                    controls: video.dataset.controls,
                    loop: video.dataset.loop,
                    modestbranding: video.dataset.branding,
                    mute: video.dataset.mute,
                    showinfo: 1,
                    rel: 0,
                    iv_load_policy: 3,
                    cc_load_policy: 0,
                    fs: 0,
                    disablekb: 1,
                },
                events: {
                    'onReady': this.onPlayerReady  // Methode innerhalb der Klasse referenzieren
                }
            });
        });
    }

    /**
     * The API will call this function when the video player is ready.
     * @param event
     */
    onPlayerReady(event) {
        event.target.playVideo();
    }

    /**
     * call playVideo Event
     */
    dispatchVideoEvent() {
        var event = document.createEvent("Event");
        event.initEvent("playVideo", false, true);
        document.dispatchEvent(event);
    }
}
